import { ref } from "vue";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { RSFindingsColumns } from "@/types/report-settings.types";

export type FindingsColumnKey = RSFindingsColumns | "photos" | "actions";

// Columns configurable by the user (order + visibility)
const CONFIGURABLE_COLUMNS: FindingsColumnKey[] = [
  RSFindingsColumns.NOTATION,
  RSFindingsColumns.DEFAULT_NUMBERING,
  RSFindingsColumns.NAME,
  RSFindingsColumns.COMMENT,
  RSFindingsColumns.LOCATION,
  RSFindingsColumns.EVOLUTION,
  RSFindingsColumns.SECURITY,
  "photos",
];

const DEFAULT_VISIBLE: FindingsColumnKey[] = [
  RSFindingsColumns.NOTATION,
  RSFindingsColumns.DEFAULT_NUMBERING,
  RSFindingsColumns.NAME,
  RSFindingsColumns.COMMENT,
  RSFindingsColumns.EVOLUTION,
  RSFindingsColumns.SECURITY,
  "photos",
];

function loadColumns(): FindingsColumnKey[] {
  const stored = $ls.getItem(LocalStorageKey.FINDINGS_TABLE_COLUMNS);
  if (!stored) return DEFAULT_VISIBLE;
  try {
    const parsed = JSON.parse(stored) as FindingsColumnKey[];
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_VISIBLE;
    // Filter out invalid values and 'actions' (always shown separately)
    return parsed.filter((col) => CONFIGURABLE_COLUMNS.includes(col));
  } catch {
    return DEFAULT_VISIBLE;
  }
}

const visibleColumns = ref<FindingsColumnKey[]>(loadColumns());

export function useFindingsTableColumns() {
  const setVisibleColumns = (columns: FindingsColumnKey[]) => {
    visibleColumns.value = columns;
    $ls.setItem(
      LocalStorageKey.FINDINGS_TABLE_COLUMNS,
      JSON.stringify(columns)
    );
  };

  const isColumnVisible = (col: FindingsColumnKey) =>
    visibleColumns.value.includes(col);

  const configurableColumns = CONFIGURABLE_COLUMNS;

  return {
    visibleColumns,
    setVisibleColumns,
    isColumnVisible,
    configurableColumns,
  };
}
